
import { defineComponent, nextTick } from 'vue'
import BasicCard from '@/components/cards/BasicCard.vue'
import ApiService from '@/services/ApiService'
import { Dossier } from '@/types/portal'
import { SfpSignatureStatus } from '@/enums/SfpSignatureEnums'
import CardAccordion from '@/components/accordions/CardAccordion.vue'
import dateFormat from 'dateformat'
import WizardItem from '@/components/wizard/WizardItem.vue'
import WizardContainer from '@/components/wizard/WizardContainer.vue'
import { useI18n } from 'vue-i18n'
import { changeDateLocalization } from '@/config/i18n/date'
import { ElementAnimateUtil } from '@/assets/metronik/ts/_utils'
import FileInput from '@/components/forms/FileInput.vue'
import AdmissibleDocuments from '@/views/sub/AdmissibleDocuments.vue'
import { useStore } from 'vuex'
import { CmsResponse } from '@/types/api-response'
import { Actions } from '@/store/enums/StoreEnums'
import SimpleCard from '@/components/cards/SimpleCard.vue'

export default defineComponent({
  name: 'PortalCases',
  components: { SimpleCard, AdmissibleDocuments, WizardContainer, WizardItem, CardAccordion, BasicCard },
  data: function () {
    return {
      dossiers: [] as Dossier[]
    }
  },
  mounted () {
    ElementAnimateUtil.scrollTop(0, 500)
    const i18n = useI18n()
    const store = useStore()
    changeDateLocalization(i18n.locale.value)

    nextTick(() => {
      ApiService.get<Dossier[]>('/admissibles/portal/dossiers').then((response) => {
        this.dossiers = response.data
      })
    })

    store.dispatch(Actions.FETCH_CMS_DATA)
  },
  computed: {
    cmsData (): CmsResponse {
      return useStore().getters.getCmsData
    },
    canManageContract (): boolean {
      return this.cmsData.cfaPermissions?.contractManagement ?? false
    },
    sfpDossiers (): Dossier[] {
      return this.dossiers.filter(dossier =>
        dossier.etapeStatus?.some(e => e.type === 'sfp')
      )
    }
  },
  methods: {
    formatDate (date: string) {
      try {
        if (window.innerWidth < 768) {
          return dateFormat(date, 'dd/MM/yyyy')
        } else {
          return dateFormat(date, 'dddd dd mmmm yyyy')
        }
      } catch (e) {
        return date
      }
    },
    handleClick (event: Event) {
      this.$router.push((event.currentTarget as HTMLElement).attributes.getNamedItem('data-href')?.value ?? '#')
      event.stopPropagation()
      event.preventDefault()
    },
    downloadPdf (event: Event, url: string) {
      ApiService.get<string>(url, { responseType: 'blob' }).then((response) => {
        const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        window.open(objectUrl)
      })

      event.stopPropagation()
      event.preventDefault()
    },
    getSfpBadge (dossier: Dossier) {
      return dossier.etapeStatus?.find(etape => etape.type === 'sfp')?.status
    },
    getSfpEtapeStatus (dossier: Dossier) {
      const status = this.getSfpBadge(dossier)
      return status ? `${status}` : null
    },
    isSfpConventionToSign (dossier: Dossier): boolean {
      return this.getSfpBadge(dossier) === SfpSignatureStatus.TO_SIGN
    },
    isSfpConventionSigned (dossier: Dossier): boolean {
      return this.getSfpBadge(dossier) === SfpSignatureStatus.SIGNED
    },
    getSfpBadgeClass (dossier: Dossier) {
      switch (this.getSfpBadge(dossier)) {
        case SfpSignatureStatus.DRAFTING:
          return 'badge-light'
        case SfpSignatureStatus.TO_SIGN:
          return 'badge-warning'
        case SfpSignatureStatus.SIGNED:
          return 'badge-success'
        default:
          return 'badge-secondary'
      }
    }
  }
})
